import { useState } from "react";
import { IconHome, IconPencil, IconPlus } from "@tabler/icons-react";

import Button from "../UI/Button";
import Card from "../UI/Card";
import Text from "../UI/Text";
import Avatar from "../UI/Avatar";
import IconButton from "../UI/IconButton";
import { Table } from "../UI/Table";
// @ts-expect-error pure js
import { useModal } from "../../hooks/use-modal.jsx";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import usePropertyFolders from "../../hooks/use-property-folders";
// @ts-expect-error pure js
import useFinanceConfigs from "../../hooks/use-finance-configs";
// @ts-expect-error pure js
import useFinanceSplitTemplates from "../../hooks/use-finance-split-templates";
// @ts-expect-error pure js
import FinanceConfigForm from "./FinanceConfigForm";
// @ts-expect-error pure js
import FinanceConfigDecoupeForm from "./FinanceConfigDecoupeForm";
// @ts-expect-error pure js
import ActionList from "../Actions/ActionList";
// @ts-expect-error pure js
import { sanitizeValues } from "../../models/utils";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
import { getParentIdPropertyFromDomain } from "../../utils/balances";
import { BalanceDomain } from "../../utils/constants/balances";
import { getInitialsFromName } from "../Profile/profileUtils";
import style from "./FinanceConfigList.module.css";
import Title from "../UI/Title.tsx";
import AplConfigCard from "../Tenant/Apl/AplConfigCard";

type ParentEntity = {
  id: number;
  nom?: string;
  id_bien?: number;
  image?: string;
};

type Domain = string;

type FinanceConfig = {
  id: number;
  domaine: Domain;
  keyword: string;
  [key: string]: unknown;
};

type Split = {
  id: number;
  domaine: Domain;
  montant: number;
  pourcentage?: number;
  [key: string]: unknown;
};

type SplitTemplate = {
  id: number;
  nom: string;
  splits: Split[];
};

type FinanceConfigListProps = {
  displayNom?: boolean;
  tenantId?: number;
};

export default function FinanceConfigList({
  displayNom,
  tenantId,
}: FinanceConfigListProps) {
  const { tenantsById } = useTenants({ withById: true });
  const { propertiesById } = useProperties({ withById: true });
  const { propertyFoldersById } = usePropertyFolders({ withById: true });

  const [FormModal, closeFormModal, openFormModal] = useModal();
  const [FormDecoupeModal, closeFormDecoupeModal, openFormDecoupeModal] =
    useModal();

  const [configToEdit, setConfigToEdit] = useState<FinanceConfig | null>(null);
  const [splitTemplateToEdit, setSplitTemplateToEdit] =
    useState<SplitTemplate | null>(null);

  const {
    financeConfigs,
    deleteFinanceConfig,
    updateFinanceConfig,
    addFinanceConfig,
  } = useFinanceConfigs(tenantId);

  const {
    splitTemplates,
    updateFinanceSplitTemplate,
    addFinanceSplitTemplate,
    deleteFinanceSplitTemplate,
  } = useFinanceSplitTemplates();

  async function deleteFinanceConfigHandler(configId: number) {
    try {
      await deleteFinanceConfig(configId);
      closeFormModal();
    } catch (e) {
      console.error(e);
    }
  }

  async function deleteFinanceSplitTemplateHandler(configId: number) {
    try {
      await deleteFinanceSplitTemplate(configId);
      closeFormDecoupeModal();
    } catch (e) {
      console.error(e);
    }
  }

  async function submitSplitTemplateHandler(values: SplitTemplate) {
    const sanitizedValues = sanitizeValues(values) as SplitTemplate;
    if (sanitizedValues.id) {
      await updateFinanceSplitTemplate(sanitizedValues.id, sanitizedValues);
    } else {
      await addFinanceSplitTemplate(sanitizedValues);
    }
    closeFormDecoupeModal();
    setSplitTemplateToEdit(null);
  }

  async function submitFinanceConfigHandler(values: FinanceConfig) {
    const sanitizedValues = sanitizeValues(values) as FinanceConfig;
    if (sanitizedValues.id) {
      await updateFinanceConfig(sanitizedValues.id, sanitizedValues);
    } else {
      await addFinanceConfig(sanitizedValues);
    }
    closeFormModal();
    setConfigToEdit(null);
  }

  function getParentDictForDomain(
    domain: Domain,
  ): Record<number, ParentEntity> {
    switch (domain) {
      case BalanceDomain.PROPERTY:
        return propertiesById;
      case BalanceDomain.TENANT:
        return tenantsById;
      case BalanceDomain.FOLDER:
        return propertyFoldersById;
      default:
        return {};
    }
  }

  function getDisplayName(
    parent: ParentEntity | undefined,
    domain: Domain,
  ): string {
    if (!parent) return "";
    switch (domain) {
      case BalanceDomain.PROPERTY:
      case BalanceDomain.TENANT:
      case BalanceDomain.FOLDER:
        return parent?.nom || "";
      default:
        return "";
    }
  }

  function getPropertyName(
    parent: ParentEntity | undefined,
    domain: Domain,
  ): string | null {
    if (!parent) return null;
    if (domain === BalanceDomain.TENANT && parent.id_bien) {
      return propertiesById[parent.id_bien]?.nom ?? null;
    }
    return null;
  }

  function getParentForConfig(
    config: FinanceConfig | Split,
  ): ParentEntity | undefined {
    const parentDict = getParentDictForDomain(config.domaine);
    const parentId = config[
      getParentIdPropertyFromDomain(config.domaine) as string
    ] as number | undefined;
    return parentId !== undefined ? parentDict[parentId] : undefined;
  }

  return (
    <>
      <FormModal size="medium">
        <FinanceConfigForm
          config={configToEdit}
          tenantId={tenantId}
          onDelete={deleteFinanceConfigHandler}
          onSubmit={submitFinanceConfigHandler}
        />
      </FormModal>
      <FormDecoupeModal size="ultra">
        <FinanceConfigDecoupeForm
          config={splitTemplateToEdit}
          onDelete={deleteFinanceSplitTemplateHandler}
          onSubmit={submitSplitTemplateHandler}
        />
      </FormDecoupeModal>

      <div className={style.wrapper}>
        <Card padding="md">
          <div className={style.cardHeader}>
            <Title variant="deca" weight="bold">
              Reconnaissance automatique des paiements
            </Title>
            <Text color="muted">
              Connectez votre compte bancaire pour identifier automatiquement
              les virements de loyer. Si le libellé du virement est différent du
              nom du locataire (CAF, conjoint, autre compte), vous pouvez créer
              une règle pour l'associer correctement.
            </Text>
          </div>

          <Button
            onClick={() => {
              setConfigToEdit(null);
              openFormModal();
            }}
            icon={IconPlus}
          >
            Ajouter une règle
          </Button>

          {financeConfigs.length > 0 && (
            <Table
              variant="card"
              expandable
              rowCount={financeConfigs.length}
              className={style.financeConfigTable}
            >
              <Table.Header>
                {displayNom && <Table.Column>Affectation</Table.Column>}
                <Table.Column>Libellé</Table.Column>
                <Table.Column align="right">Actions</Table.Column>
              </Table.Header>
              <Table.Body>
                {financeConfigs.map((config: FinanceConfig) => {
                  const parent = getParentForConfig(config);
                  const name = getDisplayName(parent, config.domaine);
                  const propertyName = getPropertyName(parent, config.domaine);
                  const initials = getInitialsFromName(name);

                  return (
                    <Table.Row key={config.id}>
                      {displayNom && (
                        <Table.Cell>
                          <div className={style.affectationCell}>
                            <Avatar initials={initials} size="sm" />
                            <div>
                              <Text variant="deci" weight="medium">
                                {name}
                              </Text>
                              {propertyName && (
                                <Text variant="deci" color="muted">
                                  {propertyName}
                                </Text>
                              )}
                            </div>
                          </div>
                        </Table.Cell>
                      )}
                      <Table.Cell>
                        <Text variant="deci" weight="bold">
                          {config.keyword}
                        </Text>
                      </Table.Cell>
                      <Table.Cell align="right">
                        <IconButton
                          icon={IconPencil}
                          label="Modifier"
                          onClick={() => {
                            setConfigToEdit(config);
                            openFormModal();
                          }}
                          className={style.action}
                        />
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          )}
        </Card>

        {tenantId && <AplConfigCard tenantId={tenantId} />}

        {tenantId ? (
          <ActionList tenant={tenantsById[tenantId]} />
        ) : (
          <Card padding="md">
            <div className={style.cardHeader}>
              <Title variant="deca" weight="bold">
                Automatiser la répartition des paiements
              </Title>
              <Text color="muted">
                Répartissez une transaction entre locataires, biens ou dossiers.
                Le montant s'ajuste automatiquement selon la règle définie. Vous
                pouvez aussi ajouter un document CAF pour créer la répartition.
              </Text>
            </div>

            <Button
              onClick={() => {
                setSplitTemplateToEdit(null);
                openFormDecoupeModal();
              }}
              icon={IconPlus}
            >
              Ajouter une règle
            </Button>

            {splitTemplates && splitTemplates.length > 0 && (
              <div className={style.rulesContainer}>
                {splitTemplates.map((template: SplitTemplate) => (
                  <div key={template.id} className={style.ruleBlock}>
                    <div className={style.ruleHeader}>
                      <Text variant="deca" weight="bold">
                        {template.nom}
                      </Text>
                      <IconButton
                        onClick={() => {
                          setSplitTemplateToEdit(template);
                          openFormDecoupeModal();
                        }}
                        label="Modifier"
                        icon={IconPencil}
                      />
                    </div>

                    <Text weight="medium">Répartition du paiement</Text>

                    <Table variant="card" className={style.ruleSplits}>
                      <Table.Body>
                        {template?.splits.map((split: Split) => {
                          const splitParent = getParentForConfig(split);
                          const splitName = getDisplayName(
                            splitParent,
                            split.domaine,
                          );
                          const splitPropertyName = getPropertyName(
                            splitParent,
                            split.domaine,
                          );
                          const splitInitials = getInitialsFromName(splitName);

                          return (
                            <Table.Row key={split.id}>
                              <Table.Cell>
                                <div className={style.affectationCell}>
                                  <Avatar
                                    initials={
                                      split.domaine === BalanceDomain.TENANT
                                        ? splitInitials
                                        : undefined
                                    }
                                    icon={
                                      split.domaine !== BalanceDomain.TENANT ? (
                                        <IconHome size={20} />
                                      ) : undefined
                                    }
                                    size="sm"
                                  />
                                  <div>
                                    <Text variant="deci" weight="medium">
                                      {splitName}
                                    </Text>
                                    {splitPropertyName && (
                                      <Text variant="deci" color="muted">
                                        {splitPropertyName}
                                      </Text>
                                    )}
                                  </div>
                                </div>
                              </Table.Cell>
                              <Table.Cell align="right">
                                <Text variant="deci" weight="bold">
                                  {currencyFormatter(split.montant)}
                                </Text>
                                {split.pourcentage && (
                                  <Text
                                    variant="micro"
                                    weight="medium"
                                    color="muted"
                                  >
                                    {split.pourcentage}% du paiement
                                  </Text>
                                )}
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>
    </>
  );
}
